/* global fetch, requestAnimationFrame */
import React, { useState, useEffect, useRef } from 'react'
import { createUseStyles } from 'react-jss'
import theme from '../style/theme'
import cn from 'classnames'
import SubscriptionWidget from './Slices/ShopProducts/SubscriptionWidget'
import BuyButton from './Slices/ShopProducts/BuyButton'
import RelatedProducts from './RelatedProducts'
import AccordionItem from './AccordionItem'
import useStickyElement from './useStickyElement'
import { getPageYOffset, setPageYOffset, scrollbar } from './useSmoothScrollbar'
import YotpoStarRatings from './YotpoStarRatings'

const ProductDetail = ({ product, isLoading, ingredients, nutritionalInfo, description, relatedProducts }) => {
  const classes = useStyles()
  const [selectedVariantId, setSelectedVariantId] = useState(null)
  const [sellingPlanId, setSellingPlanId] = useState(null)
  const [selectedPrice, setSelectedPrice] = useState(0)
  const [isSubscription, setIsSubscription] = useState(true)
  const [sellingPlans, setSellingPlans] = useState([])
  const [quantity, setQuantity] = useState(1)
  const [activeAccordion, setActiveAccordion] = useState('details')
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)

  // Ref for debouncing intersection observer updates
  const observerTimeoutRef = useRef(null)

  // Ref to track if user manually clicked a thumbnail (to temporarily disable observer)
  const isManualSelectionRef = useRef(false)

  // Refs for sticky thumbnail functionality
  const thumbnailWrapperRef = useRef(null)
  const thumbnailContainerRef = useRef(null)
  // Initialize sticky element for thumbnail container
  useStickyElement(thumbnailWrapperRef, thumbnailContainerRef)

  // Helper function to check if product is one-time only
  const isOneTimeOnlyProduct = (product) => {
    if (!product) return false
    const productType = product?.store?.productType?.toLowerCase() || ''
    const productTags = product?.store?.tags?.toLowerCase() || ''
    // Define keywords that indicate one-time only products
    const oneTimeKeywords = ['one-time', 'onetime', 'one-time-only']
    // Check if product should be one-time only
    return oneTimeKeywords.some(keyword =>
      productType.includes(keyword) || productTags.includes(keyword)
    )
  }

  // Add intersection observer to track visible images
  useEffect(() => {
    // Check if IntersectionObserver is available (browser compatibility)
    if (typeof window === 'undefined' || !window.IntersectionObserver) {
      return
    }

    // Use a delay to ensure the gallery is rendered
    const setupObserver = () => {
      const imageContainer = document.querySelector(`.${classes.verticalImagesContainer}`)
      const imageItems = document.querySelectorAll(`.${classes.verticalImageItem}`)

      if (imageItems.length === 0 || !imageContainer) return

      const observer = new window.IntersectionObserver(
        (entries) => {
          // Skip if user just manually selected an image
          if (isManualSelectionRef.current) {
            return
          }

          // Get all currently intersecting entries with their ratios
          const intersectingEntries = entries
            .filter(entry => entry.isIntersecting)
            .map(entry => ({
              index: parseInt(entry.target.getAttribute('data-image-index')),
              ratio: entry.intersectionRatio,
              target: entry.target
            }))
            .filter(entry => !isNaN(entry.index))

          if (intersectingEntries.length === 0) return

          // Sort by intersection ratio (highest first)
          intersectingEntries.sort((a, b) => b.ratio - a.ratio)

          // If we have a clear winner (significantly higher ratio), use it
          const topEntry = intersectingEntries[0]

          // If there are multiple entries with similar ratios, choose based on scroll position
          if (intersectingEntries.length > 1) {
            const secondEntry = intersectingEntries[1]
            const ratioDifference = topEntry.ratio - secondEntry.ratio

            // If the difference is small (less than 0.1), choose based on which image center is closer to viewport center
            if (ratioDifference < 0.1) {
              const viewportCenter = window.innerHeight / 2

              let closestEntry = topEntry
              let closestDistance = Infinity

              intersectingEntries.forEach(entry => {
                const imageRect = entry.target.getBoundingClientRect()
                const imageCenter = imageRect.top + imageRect.height / 2
                const distance = Math.abs(imageCenter - viewportCenter)

                if (distance < closestDistance) {
                  closestDistance = distance
                  closestEntry = entry
                }
              })

              // Debounce the update to prevent rapid changes
              if (observerTimeoutRef.current) {
                clearTimeout(observerTimeoutRef.current)
              }

              observerTimeoutRef.current = setTimeout(() => {
                setSelectedImageIndex(closestEntry.index)
              }, 50)
              return
            }
          }

          // Use the entry with the highest intersection ratio
          const newIndex = topEntry.index

          // Debounce the update to prevent rapid changes
          if (observerTimeoutRef.current) {
            clearTimeout(observerTimeoutRef.current)
          }

          observerTimeoutRef.current = setTimeout(() => {
            setSelectedImageIndex(newIndex)
          }, 50)
        },
        {
          root: null, // Use viewport as root instead of imageContainer
          threshold: [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9], // More granular thresholds
          rootMargin: '-10% 0px -10% 0px' // Smaller margin for more precise detection
        }
      )

      imageItems.forEach((item) => observer.observe(item))

      return () => {
        imageItems.forEach((item) => observer.unobserve(item))
      }
    }

    const timer = setTimeout(setupObserver, 100)
    return () => {
      clearTimeout(timer)
      // Clear manual selection flag on cleanup
      isManualSelectionRef.current = false
      // Clear any pending observer timeout
      if (observerTimeoutRef.current) {
        clearTimeout(observerTimeoutRef.current)
      }
    }
  }, [classes.verticalImageItem, classes.verticalImagesContainer, product]) // Re-run when product changes

  // Debug effect to track sellingPlanId changes
  useEffect(() => {
    // console.log('🔄 ProductDetail: sellingPlanId state changed:', sellingPlanId)
  }, [sellingPlanId])

  useEffect(() => {
    const variantId = product?.store?.variants?.[0]?.id
    setSelectedVariantId(variantId)
  }, [product])

  // Initialize selectedPrice with product's default price
  useEffect(() => {
    if (product?.store?.priceRange?.minVariantPrice && selectedPrice === 0) {
      const defaultPrice = product.store.priceRange.minVariantPrice
      // console.log('🔄 ProductDetail: Initializing selectedPrice with default price:', defaultPrice)
      setSelectedPrice(defaultPrice)
    }
  }, [product, selectedPrice])

  useEffect(() => {
    const fetchSellingPlans = async () => {
      try {
        if (!product?.store?.id) return
        // console.log('🔍 Fetching Recharge plans for product ID:', product.store.id)
        const response = await fetch(`https://api-server.discoveredfoods.com/api/getRechargePlans?productId=${product.store.id}`)

        if (!response.ok) {
          console.error('❌ Recharge API response not OK:', response.status, response.statusText)
          setSellingPlans([])
          return
        }

        const data = await response.json()
        // console.log('📦 Subscription plans received:', data)

        if (data && Array.isArray(data)) {
          setSellingPlans(data)
        } else {
          console.error('❌ Unexpected data format from Recharge API:', data)
          setSellingPlans([])
        }
      } catch (error) {
        console.error('❌ Error fetching Recharge plans:', error)
        setSellingPlans([])
      }
    }

    fetchSellingPlans()
  }, [product])

  // Handle one-time only products
  useEffect(() => {
    if (product && isOneTimeOnlyProduct(product)) {
      // If it's a one-time only product, ensure we're in one-time purchase mode
      console.log('🔒 One-time only product detected:', {
        productType: product?.store?.productType,
        productTags: product?.store?.tags,
        productTitle: product?.store?.title || product?.title
      })
      setIsSubscription(false)
      setSellingPlanId(null)
      if (product?.store?.priceRange?.minVariantPrice) {
        setSelectedPrice(product.store.priceRange.minVariantPrice)
      }
    }
  }, [product])

  const handleSubscriptionSelection = (planId, currentPrice, isSubscriptionSelected) => {
    // console.log('🔄 ProductDetail: Received selling plan ID from SubscriptionWidget:', planId)
    // console.log('🔄 ProductDetail: Received current price:', currentPrice)
    // console.log('🔄 ProductDetail: Is subscription:', isSubscriptionSelected)
    setSellingPlanId(planId)
    setSelectedPrice(currentPrice)
    setIsSubscription(isSubscriptionSelected)
  }

  const handleAccordionClick = (section) => {
    setActiveAccordion(activeAccordion === section ? null : section)
  }

  // Function to scroll to selected image when thumbnail is clicked
  const scrollToImage = (index) => {
    // console.log('🖼️ Scrolling to image index:', index)

    // Set manual selection flag to prevent observer interference
    isManualSelectionRef.current = true
    setSelectedImageIndex(index)

    // Use requestAnimationFrame for better performance
    requestAnimationFrame(() => {
      const targetImage = document.querySelector(`.${classes.verticalImageItem}[data-image-index="${index}"]`)

      // console.log('🎯 Target image found:', targetImage)

      if (targetImage) {
        // Get the target image's position relative to the viewport
        const targetImageRect = targetImage.getBoundingClientRect()
        const currentScrollY = getPageYOffset()
        const viewportHeight = window.innerHeight

        // Calculate if the image is already visible in viewport
        const imageTop = targetImageRect.top
        const imageBottom = targetImageRect.bottom

        // console.log('🔍 Image position check:', {
        //   imageTop,
        //   imageBottom,
        //   viewportHeight,
        //   currentScrollY,
        //   targetImageAbsoluteTop: targetImageRect.top + currentScrollY
        // })

        let scrollAdjustment = 0

        // If image is above the viewport (top is negative), scroll up to show it
        if (imageTop < 120) {
          scrollAdjustment = imageTop - 120
        } else if (imageBottom > viewportHeight - 120) {
          scrollAdjustment = imageBottom - viewportHeight + 120 // Add margin from bottom
        } else {
          // console.log('✅ Image is already well positioned, no scrolling needed')
        }

        // Only scroll if adjustment is needed
        if (scrollAdjustment !== 0) {
          const newScrollY = currentScrollY + scrollAdjustment

          // Get actual document height (handle smooth-scrollbar case)
          let documentHeight = document.documentElement.scrollHeight || document.body.scrollHeight

          // If smooth-scrollbar is active, the document height might be reported incorrectly
          // Try to get the actual content height by checking the last image's position
          const lastImage = document.querySelector(`.${classes.verticalImageItem}:last-child`)
          if (lastImage && documentHeight <= viewportHeight) {
            const lastImageRect = lastImage.getBoundingClientRect()
            const lastImageBottom = lastImageRect.bottom + currentScrollY
            documentHeight = Math.max(documentHeight, lastImageBottom + 200) // Add some padding
          }

          const maxScroll = Math.max(0, documentHeight - viewportHeight)

          // Don't clamp the scroll position if we're trying to reach a valid target
          // Just ensure we don't go beyond the document bounds
          let finalScrollTop = newScrollY
          if (finalScrollTop < 0) finalScrollTop = 0
          if (finalScrollTop > maxScroll && maxScroll > 0) finalScrollTop = maxScroll

          // console.log('📜 Scrolling calculation:', {
          //   currentScrollY,
          //   scrollAdjustment,
          //   newScrollY,
          //   documentHeight,
          //   viewportHeight,
          //   maxScroll,
          //   finalScrollTop,
          //   lastImageBottom: lastImage ? (lastImage.getBoundingClientRect().bottom + currentScrollY) : 'not found'
          // })

          // Scroll the entire page using smooth-scrollbar
          try {
            // console.log('🔧 About to scroll to:', finalScrollTop)

            // Debug scrollbar availability
            // console.log('🔍 Scrollbar debug:', {
            //   importedScrollbar: !!scrollbar,
            //   windowScrollbar: !!(typeof window !== 'undefined' && window.scrollbar),
            //   windowScrollbarClass: !!(typeof window !== 'undefined' && window.Scrollbar),
            //   scrollbarInstances: typeof window !== 'undefined' && window.Scrollbar && window.Scrollbar.getAll ? window.Scrollbar.getAll().length : 0
            // })

            // Try using the scrollbar instance from the global export first
            if (scrollbar) {
              // console.log('🎯 Using imported scrollbar instance for scroll')
              scrollbar.scrollTo(0, finalScrollTop, 600) // 600ms smooth scroll
            } else if (typeof window !== 'undefined' && window.scrollbar) {
              // console.log('🎯 Using window.scrollbar instance for scroll')
              window.scrollbar.scrollTo(0, finalScrollTop, 600) // 600ms smooth scroll
            } else if (typeof window !== 'undefined' && window.Scrollbar) {
              const scrollInstances = window.Scrollbar.getAll ? window.Scrollbar.getAll() : []

              if (scrollInstances.length > 0) {
                const mainScrollInstance = scrollInstances[0]
                // console.log('🎯 Using Scrollbar.getAll() instance for scroll')
                mainScrollInstance.scrollTo(0, finalScrollTop, 600) // 600ms smooth scroll
              } else {
                // console.log('📋 No scroll instances found, trying setPageYOffset...')
                setPageYOffset(finalScrollTop, 600) // Use 600ms duration
              }
            } else {
              // console.log('🔄 No Scrollbar available, using setPageYOffset...')
              setPageYOffset(finalScrollTop, 600) // Use 600ms duration
            }

            // console.log('✅ Scroll command executed')

            // Verify scroll happened after animation completes
            setTimeout(() => {
              const actualScrollY = getPageYOffset()
              // console.log('🔍 Scroll verification - Expected:', finalScrollTop, 'Actual:', actualScrollY)

              // If scroll still didn't work, try window.scrollTo as final fallback
              if (Math.abs(actualScrollY - finalScrollTop) > 50) {
                // console.log('🔄 Fallback to window.scrollTo...')
                window.scrollTo({
                  top: finalScrollTop,
                  behavior: 'smooth'
                })

                // Final verification
                setTimeout(() => {
                  // const finalScrollY = getPageYOffset()
                  // console.log('🔍 Final scroll verification - Expected:', finalScrollTop, 'Actual:', finalScrollY)
                }, 600)
              }
            }, 700) // Wait for smooth scroll animation to complete
          } catch (error) {
            console.error('❌ Error during scroll:', error)
            // Emergency fallback
            window.scrollTo({
              top: finalScrollTop,
              behavior: 'smooth'
            })
          }
        }

        // Clear the manual selection flag after scroll animation completes
        setTimeout(() => {
          isManualSelectionRef.current = false
          // console.log('🔓 Manual selection flag cleared')
        }, 1000) // Increased timeout to ensure scroll completes

        // console.log('✅ Scroll adjustment applied for image', index, {
        //   imageTop,
        //   imageBottom,
        //   viewportHeight,
        //   scrollAdjustment,
        //   finalScrollTop: scrollAdjustment !== 0 ? currentScrollY + scrollAdjustment : 'no scroll needed'
        // })
      } else {
        // Clear flag even if scroll fails
        isManualSelectionRef.current = false
        console.error('❌ Target image not found for index', index)
        // console.log('Available images:', document.querySelectorAll(`.${classes.verticalImageItem}`))
      }
    })
  }

  const accordionData = [
    {
      id: 'details',
      title: 'Product Details',
      content: (
        <>
          {product.store?.descriptionHtml && (
            <div
              className={classes.description}
              dangerouslySetInnerHTML={{ __html: product.store.descriptionHtml }}
            />
          )}
          {description?.description && (
            <div className={classes.description}>
              <div dangerouslySetInnerHTML={{ __html: description.description }} />
            </div>
          )}
        </>
      )
    },
    {
      id: 'ingredients',
      title: 'Ingredients',
      content: (
        <>
          {/* First check ingredients prop from slice */}
          {ingredients?.ingredients?.length > 0 ? (
            <div className={classes.ingredientsList}>
              {ingredients.ingredients.map((item, index) => (
                <div key={index} className={classes.ingredientItem}>
                  <span className={classes.ingredientName}>{item.ingredient}</span>
                  {item.amount && <span className={classes.ingredientAmount}>{item.amount}</span>}
                </div>
              ))}
            </div>
          ) : product?.ingredientsSection?.ingredients?.length > 0 ? (
            <div className={classes.ingredientsList}>
              {product.ingredientsSection.ingredients.map((item, index) => (
                <div key={index} className={classes.ingredientItem}>
                  <span className={classes.ingredientName}>
                    {typeof item === 'string' ? item : item.ingredient}
                  </span>
                  {typeof item === 'object' && item.amount && (
                    <span className={classes.ingredientAmount}>{item.amount}</span>
                  )}
                </div>
              ))}
            </div>
          ) : product.store?.ingredients ? (
            <div className={classes.ingredients}>
              {typeof product.store.ingredients === 'string'
                ? product.store.ingredients
                : typeof product.store.ingredients === 'object' && product.store.ingredients.ingredients
                  ? (
                    <div className={classes.ingredientsList}>
                      {product.store.ingredients.ingredients.map((item, index) => (
                        <div key={index} className={classes.ingredientItem}>
                          <span className={classes.ingredientName}>
                            {typeof item === 'string' ? item : item.ingredient}
                          </span>
                          {typeof item === 'object' && item.amount && (
                            <span className={classes.ingredientAmount}>{item.amount}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )
                  : 'Ingredients information available.'}
            </div>
          ) : (
            'Ingredients information coming soon.'
          )}
        </>
      )
    },
    {
      id: 'nutrition',
      title: 'Nutritional Information',
      content: (
        <>
          {nutritionalInfo?.facts?.length > 0 ? (
            <div className={classes.nutritionTable}>
              {nutritionalInfo.servingSize && (
                <p className={classes.servingSize}>Serving Size: {nutritionalInfo.servingSize}</p>
              )}
              {nutritionalInfo.facts.map((fact, index) => (
                <div key={index} className={classes.nutritionRow}>
                  <span className={classes.nutrientName}>{fact.nutrient}</span>
                  <span className={classes.nutrientAmount}>{fact.amount}</span>
                  {fact.dailyValue && (
                    <span className={classes.dailyValue}>{fact.dailyValue}%</span>
                  )}
                </div>
              ))}
            </div>
          ) : product?.nutritionalInfo ? (
            <div className={classes.nutrition}>
              {typeof product.nutritionalInfo === 'string'
                ? product.nutritionalInfo
                : typeof product.nutritionalInfo === 'object' && product.nutritionalInfo.facts
                  ? (
                    <>
                      {product.nutritionalInfo.servingSize && (
                        <p className={classes.servingSize}>Serving Size: {product.nutritionalInfo.servingSize}</p>
                      )}
                      {product.nutritionalInfo.facts.map((fact, index) => (
                        <div key={index} className={classes.nutritionRow}>
                          <span className={classes.nutrientName}>{fact.nutrient}</span>
                          <span className={classes.nutrientAmount}>{fact.amount}</span>
                          {fact.dailyValue && (
                            <span className={classes.dailyValue}>{fact.dailyValue}%</span>
                          )}
                        </div>
                      ))}
                    </>
                  )
                  : 'Nutritional information available.'}
            </div>
          ) : (
            'Nutritional information coming soon.'
          )}
        </>
      )
    }
  ]
  // Add debug logging to see full product data
  // console.log('Full product data:', product)
  // console.log('Product slug data:', {
  //   storeSlug: product?.store?.slug,
  //   directSlug: product?.slug,
  //   usingSlug: product?.store?.slug || product?.slug
  // })

  // // Debug weight and short description from Sanity
  // console.log('Weight from Sanity:', product?.store?.weight)
  // console.log('Short description from Sanity:', product?.store?.shortDescription)
  if (!product && !isLoading) {
    window.location.href = '/'
    return null
  }
  if (isLoading) {
    return (
      <div className={classes.container}>
        <div className={cn(classes.productDetail, classes.loading)}>
          <div className={classes.loadingPulse}> </div>
        </div>
      </div>
    )
  }
  const productTitle = product.store?.title || product.title

  // Enhanced image gallery handling - combine Shopify and Sanity gallery images
  const productImages = product.store?.images || []
  const sanityGalleryImages = product?.galleryImages || [] // Fixed: access from store object

  // Process Sanity gallery images into the same format as Shopify images
  const processedSanityImages = sanityGalleryImages.map(img => {
    // Use the largest available size for src
    const largestSize = Array.isArray(img.image?.sizes) && img.image.sizes.length > 0
      ? img.image.sizes[img.image.sizes.length - 1]
      : null
    const src = largestSize ? largestSize.url : img.image?.asset?.url || ''
    // Build srcset from available sizes
    const srcset = Array.isArray(img.image?.sizes)
      ? img.image.sizes.map(size => `${size.url} ${size.width}w`).join(', ')
      : src ? `${src} 1200w` : ''
    return {
      src,
      srcset,
      sizes: '(max-width: 768px) 100vw, 50vw',
      alt: img.image?.altText || productTitle || 'Product image',
      loading: 'eager'
    }
  })

  // Process Shopify images
  const processedShopifyImages = productImages.map(img => ({
    src: img.url,
    srcset: [
        `${img.url}&width=400 400w`,
        `${img.url}&width=800 800w`,
        `${img.url}&width=1200 1200w`
    ].join(', '),
    sizes: '(max-width: 768px) 100vw, 50vw',
    alt: img.altText || productTitle || 'Product image',
    loading: 'eager',
    width: img.width,
    height: img.height
  }))

  // Combine all images: Sanity gallery images first, then Shopify images
  const allImages = [...processedSanityImages, ...processedShopifyImages]

  const hasGallery = allImages.length > 0

  // Use combined gallery images if available, otherwise fallback to preview image
  const images = hasGallery
    ? allImages
    : product.store?.previewImageUrl
      ? [{
        src: product.store.previewImageUrl,
        srcset: [
            `${product.store.previewImageUrl}&width=400 400w`,
            `${product.store.previewImageUrl}&width=800 800w`,
            `${product.store.previewImageUrl}&width=1200 1200w`
        ].join(', '),
        sizes: '(max-width: 768px) 100vw, 50vw',
        alt: productTitle || 'Product image',
        loading: 'eager'
      }]
      : []

  // console.log('Product images debug:', {
  //   shopifyImages: productImages.length,
  //   sanityGalleryImages: sanityGalleryImages.length,
  //   totalImages: images.length,
  //   hasGallery,
  //   selectedImageIndex,
  //   sanityGalleryData: sanityGalleryImages,
  //   shopifyImageData: productImages,
  //   productStoreData: product.store // Added to help debug the full store object
  // })

  const priceRange = product.store?.priceRange || {}
  const price = priceRange.minVariantPrice
  const status = product.store?.status
  const available = status === 'active'
  const comingSoon = status === 'draft'
  return (
    <main className={classes.container}>
      <div key={product._id} className={classes.productDetail}>
        <div className={classes.productGrid}>
          <div className={classes.imageSection}>
            {images.length > 0 ? (
              <>
                {/* Desktop: Vertical Gallery */}
                <div className={cn(classes.imageGalleryVertical, classes.desktopOnly)}>
                  {/* Vertical Image Display on the Left */}
                  <div className={classes.verticalImagesContainer}>
                    <div className={classes.verticalImagesList}>
                      {images.map((img, index) => (
                        <div
                          key={index}
                          className={cn(classes.verticalImageItem, {
                            [classes.verticalImageActive]: index === selectedImageIndex
                          })}
                          onClick={() => scrollToImage(index)}
                          data-image-index={index}
                        >
                          <img
                            src={img.src}
                            srcSet={img.srcset}
                            sizes={img.sizes}
                            alt={img.alt}
                            className={classes.verticalImage}
                            onError={(e) => {
                              console.error('Image failed to load:', img.src)
                              e.target.src = '/placeholder-product.jpg'
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Thumbnail Gallery on the Right */}
                  {images.length > 1 && (
                    <div ref={thumbnailWrapperRef} className={classes.thumbnailWrapper}>
                      <div ref={thumbnailContainerRef} className={classes.thumbnailContainerVertical}>
                        {images.map((img, index) => (
                          <button
                            key={index}
                            className={cn(classes.thumbnailVertical, {
                              [classes.thumbnailActiveVertical]: index === selectedImageIndex
                            })}
                            onClick={() => scrollToImage(index)}
                          >
                            <img
                              src={`${img.src}${img.src.includes('?') ? '&' : '?'}width=100`}
                              alt={img.alt}
                              className={classes.thumbnailImageVertical}
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Mobile: Horizontal Slider */}
                <div className={cn(classes.mobileSliderContainer, classes.mobileOnly)}>
                  <div className={classes.mobileSlider}>
                    <div
                      className={classes.mobileSliderTrack}
                      style={{
                        transform: `translateX(-${selectedImageIndex * 100}%)`,
                        width: '100%'
                      }}
                    >
                      {images.map((img, index) => (
                        <div key={index} className={classes.mobileSliderSlide}>
                          <img
                            src={img.src}
                            srcSet={img.srcset}
                            sizes={img.sizes}
                            alt={img.alt}
                            className={classes.mobileSliderImage}
                            onError={(e) => {
                              console.error('Mobile slider image failed to load:', img.src)
                              e.target.src = '/placeholder-product.jpg'
                            }}
                          />
                        </div>
                      ))}
                    </div>

                    {/* Navigation Arrows for Mobile */}
                    {images.length > 1 && (
                      <>
                        <button
                          className={cn(classes.mobileNavButton, classes.mobileNavLeft)}
                          onClick={() => {
                            const newIndex = selectedImageIndex > 0 ? selectedImageIndex - 1 : images.length - 1
                            setSelectedImageIndex(newIndex)
                          }}
                          aria-label='Previous image'
                        >
                          ←
                        </button>
                        <button
                          className={cn(classes.mobileNavButton, classes.mobileNavRight)}
                          onClick={() => {
                            const newIndex = selectedImageIndex < images.length - 1 ? selectedImageIndex + 1 : 0
                            setSelectedImageIndex(newIndex)
                          }}
                          aria-label='Next image'
                        >
                          →
                        </button>
                      </>
                    )}

                    {/* Slide counter for Mobile */}
                    {images.length > 1 && (
                      <div className={classes.mobileSlideCounter}>
                        {selectedImageIndex + 1}/{images.length}
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className={classes.noImage}>
                <span>No image available</span>
              </div>
            )}
          </div>
          <div className={classes.details}>
            {product.store?.productType && (
              <span className={classes.productType}>{product.store.productType}</span>
            )}
            <div className={classes.titleWeightRatingContainer}>
              <h1 className={classes.title}>{productTitle}</h1>

              {/* Weight/Pack Content */}
              {product?.weight && (
                <div className={classes.weightContainer}>
                  <span className={classes.weightValue}>{product.weight}</span>
                </div>
              )}
              {/* Short Description */}
              {product?.shortDescription && (
                <div className={classes.shortDescriptionContainer}>
                  <p className={classes.shortDescription}>{product.shortDescription}</p>
                </div>
              )}
              <YotpoStarRatings
                product={product}
                size='large'
                sectionId='product-detail'
                showText
                className={classes.YoptoRatingclass}
              />
            </div>

            {/* Conditionally render SubscriptionWidget based on product type */}
            {!isOneTimeOnlyProduct(product) && (
              <SubscriptionWidget
                onSelectionChange={handleSubscriptionSelection}
                price={price}
                sellingPlans={sellingPlans}
                variantId={selectedVariantId}
              />
            )}
            <div className={cn(classes.buyButton, {
              [classes.buyButtonOneTime]: isOneTimeOnlyProduct(product)
            })}
            >
              <BuyButton
                product={{
                  ...product,
                  slug: product.store?.slug || product.slug,
                  availableForSale: product.store?.status === 'active'
                }}
                variantId={selectedVariantId}
                quantity={quantity}
                setQuantity={setQuantity}
                sellingPlanId={sellingPlanId}
                selectedPrice={selectedPrice}
                isSubscription={isSubscription}
                isMainProduct
              />
            </div>

            {!available && (
              <div className={classes.availability}>
                {comingSoon ? 'Coming Soon' : 'Out of Stock'}
              </div>
            )}

            <div className={classes.accordionContainer}>
              {accordionData.map(({ id, title, content }) => (
                <AccordionItem
                  key={id}
                  title={title}
                  content={content}
                  isOpen={activeAccordion === id}
                  onClick={() => handleAccordionClick(id)}
                />
              ))}
            </div>
          </div>
        </div>        {/* Debug the product ID being passed */}
        {(() => {
          // const productIdToPass = product._id || product.id || product.store?.id
          // console.log('🔗 ProductDetail: Passing productId to RelatedProducts:', {
          //   productId: productIdToPass,
          //   product_id: product._id,
          //   product_id_field: product.id,
          //   store_id: product.store?.id,
          //   productTitle: product.store?.title || product.title,
          //   fullProduct: product
          // })
          return null
        })()}

        {/* Related Products - conditionally rendered */}
        {!relatedProducts && (
          <RelatedProducts
            productId={product._id || product.id || product.store?.id}
            title={product.store?.relatedProducts?.title}
            manualProducts={product.store?.relatedProducts?.products}
          />
        )}
        {/* YotpoReviews are now handled through YotpoReviewsSlice in ProductDetailSlice */}

        {/* Sliders are now handled in ProductDetailSlice */}
      </div>
    </main>
  )
}

const useStyles = createUseStyles({
  container: {
    maxWidth: '100%',
    margin: '0 auto',
    paddingTop: '100px',
    paddingBottom: '0rem',
    padding: '2rem 0rem',
    // Removed minHeight and alignItems to allow natural flow for sticky positioning
    display: 'flex',
    justifyContent: 'center',
    [theme.breakpoints.down('md')]: {
      paddingTop: '40px'
    }
  },
  productDetail: {
    width: '100%',
    opacity: 1,
    transition: 'opacity 0.3s ease-in-out'
  },
  loading: {
    opacity: 0.6,
    pointerEvents: 'none'
  },
  loadingPulse: {
    width: '100%',
    height: '400px',
    backgroundColor: theme.colors.lightGrey,
    animation: '$pulse 1.5s ease-in-out infinite',
    marginBottom: '2rem'
  },
  '@keyframes pulse': {
    '0%': { opacity: 0.6 },
    '50%': { opacity: 0.8 },
    '100%': { opacity: 0.6 }
  },
  productGrid: {
    maxWidth: 'calc(100% - 3rem)',
    display: 'grid',
    gridTemplateColumns: '1fr',
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: '1fr 1fr'
    },
    [theme.breakpoints.down('md')]: {
      maxWidth: '100%'
    }
  },
  ResponsiveImage: {
    maxHeight: '331px'
  },
  imageSection: {
    position: 'relative',
    // Ensure the section doesn't interfere with sticky children
    overflow: 'visible'
  },
  imageGallery: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  // Vertical Image Gallery Styles
  imageGalleryVertical: {
    display: 'flex',
    gap: '5px',
    position: 'relative',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      minHeight: 'auto'
    }
  },
  verticalImagesContainer: {
    flex: '1',
    position: 'relative',
    // Remove height constraints and overflow to let images flow naturally
    // Add custom scrollbar styling (kept for consistency but width is 0px)
    '&::-webkit-scrollbar': {
      width: '0px'
    },
    '&::-webkit-scrollbar-track': {
      background: theme.colors.lightGrey,
      borderRadius: '3px'
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.colors.grey,
      borderRadius: '3px',
      '&:hover': {
        background: theme.colors.primary
      }
    }
  },
  verticalImagesList: {
    display: 'flex',
    flexDirection: 'column',
    // Remove scrollBehavior from here since it's on the container
    '&::-webkit-scrollbar': {
      width: '0px'
    }
  },
  verticalImageItem: {
    width: '100%',
    height: '870px', // Fixed height for consistent scrolling
    position: 'relative',
    cursor: 'pointer',
    overflow: 'hidden',
    transition: 'opacity 0.2s ease',
    [theme.breakpoints.down('md')]: {
      height: '250px'
    }
  },
  verticalImageActive: {
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      pointerEvents: 'none'
    }
  },
  verticalImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    backgroundColor: theme.colors.lightGrey
  },
  thumbnailContainer: {
    display: 'flex',
    gap: '0.5rem',
    overflow: 'auto',
    paddingBottom: '0.5rem',
    scrollbarWidth: 'thin',
    [theme.breakpoints.down('md')]: {
      gap: '0.25rem'
    }
  },
  thumbnailWrapper: {
    minHeight: '100vh', // Take full viewport height
    position: 'relative',
    top: '0',
    zIndex: 10,
    [theme.breakpoints.down('md')]: {
      display: 'none' // Hide on mobile since thumbnails are not shown
    }
  },
  thumbnailContainerVertical: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    maxHeight: '70vh', // Limit height so it doesn't overflow
    overflowY: 'auto',
    // Remove position: static and let the sticky hook handle positioning
    alignSelf: 'center',
    // Add scrollbar styling for thumbnails
    '&::-webkit-scrollbar': {
      width: '4px'
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
      borderRadius: '2px'
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.colors.grey,
      borderRadius: '2px',
      '&:hover': {
        background: theme.colors.primary
      }
    },
    [theme.breakpoints.down('md')]: {
      flexDirection: 'row',
      width: '100%',
      maxHeight: 'none',
      overflowX: 'auto',
      overflowY: 'visible',
      backgroundColor: 'transparent',
      boxShadow: 'none'
    }
  },
  thumbnail: {
    flex: '0 0 auto',
    width: '51px',
    height: '67px',
    border: '2px solid transparent',
    overflow: 'hidden',
    cursor: 'pointer',
    backgroundColor: 'transparent',
    padding: 0,
    transition: 'border-color 0.2s ease',
    opacity: 0.6,

    '&:hover': {
      borderColor: theme.colors.grey
    }
  },
  thumbnailActive: {
    opacity: 1
  },
  thumbnailImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  thumbnailVertical: {
    flex: '0 0 auto',
    width: '53px',
    height: '79px',
    overflow: 'hidden',
    cursor: 'pointer',
    backgroundColor: 'transparent',
    padding: 0,
    transition: 'all 0.2s ease',
    opacity: 0.7,
    [theme.breakpoints.down('md')]: {
      width: '60px',
      height: '60px'
    },
    '&:hover': {
      borderColor: theme.colors.grey,
      opacity: 1
    }
  },
  thumbnailActiveVertical: {
    borderColor: theme.colors.primary,
    opacity: 1
  },
  thumbnailImageVertical: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    paddingLeft: '68px',
    paddingRight: '2rem',
    YoptoRatingclass: {
      justifyContent: 'flex-start !important'
    },
    // Scoped YotpoStarRatings custom styles
    '& .YotpoStarRatings-starRatingsContainer.YoptoRatingclass': {
      justifyContent: 'flex-start !important'
    },
    [theme.breakpoints.down('md')]: {
      paddingRight: '20px',
      paddingLeft: '20px',
      paddingTop: '17px'
    }
  },
  YoptoRatingclass: {
    justifyContent: 'flex-start !important'
  },
  productType: {
    fontSize: '12px',
    fontWeight: '400',
    color: theme.colors.primary,
    letterSpacing: '0.1em',
    lineHeight: '18px'
  },
  title: {
    fontSize: '48px',
    fontFamily: theme.fonts.headings,
    fontWeight: theme.fonts.headingsFontWeight,
    margin: 0,
    color: theme.colors.primary,
    [theme.breakpoints.down('md')]: {
      fontSize: '30px'
    }
  },
  titleWeightRatingContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  weightValue: {
    fontSize: '16px',
    fontWeight: '400',
    letterSpacing: '0.2px',
    color: theme.colors.primary,
    fontFamily: theme.fonts.headings
  },
  shortDescription: {
    fontSize: '14px',
    lineHeight: '22px',
    color: theme.colors.primary,
    margin: 0,
    fontFamily: theme.fonts.body,
    maxWidth: '435px'
  },
  priceContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem'
  },
  price: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: theme.colors.primary
  },
  comparePrice: {
    fontSize: '1.25rem',
    textDecoration: 'line-through',
    color: theme.colors.grey
  },
  availability: {
    display: 'inline-block',
    padding: '0.5rem 1rem',
    backgroundColor: theme.colors.error,
    color: theme.colors.white,
    borderRadius: '4px',
    fontWeight: 'bold'
  },
  buyButton: {
    marginTop: '-20px'
  },
  buyButtonOneTime: {
    borderTop: '1px solid #38524F',
    marginTop: '1rem'

  },
  description: {
    '& p': {
      margin: '1rem 0',
      lineHeight: 1.6
    }
  },
  accordionContainer: {
    marginTop: '2rem',
    borderTop: `1px solid ${theme.colors.border}`
  },
  ingredients: {
    fontSize: '1rem',
    lineHeight: 1.6
  },
  // Add ingredients list styles
  ingredientsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem'
  },
  ingredientItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.5rem 0',
    borderBottom: `1px solid ${theme.colors.lightGrey}`,
    '&:last-child': {
      borderBottom: 'none'
    }
  },
  ingredientName: {
    fontWeight: '500',
    color: theme.colors.primary
  },
  ingredientAmount: {
    color: theme.colors.grey
  },
  // Add nutrition table styles
  nutritionTable: {
    width: '100%'
  },
  nutritionRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.5rem 0',
    borderBottom: `1px solid ${theme.colors.lightGrey}`,
    '&:last-child': {
      borderBottom: 'none'
    }
  },
  nutrientName: {
    fontWeight: '500',
    color: theme.colors.primary,
    flex: 1
  },
  nutrientAmount: {
    color: theme.colors.grey,
    textAlign: 'right',
    flex: 1
  },
  dailyValue: {
    color: theme.colors.grey,
    textAlign: 'right',
    flex: 1
  },
  servingSize: {
    fontStyle: 'italic',
    color: theme.colors.grey,
    marginBottom: '1rem'
  },
  nutrition: {
    fontSize: '1rem',
    lineHeight: 1.6
  },
  // Display utility classes
  desktopOnly: {
    [theme.breakpoints.down('md')]: {
      display: 'none'
    }
  },
  mobileOnly: {
    display: 'none',
    [theme.breakpoints.down('md')]: {
      display: 'block'
    }
  },

  // Mobile Horizontal Slider Styles
  mobileSliderContainer: {
    width: '100%',
    position: 'relative'
  },
  mobileSlider: {
    width: '100%',
    height: '477px',
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: theme.colors.lightGrey
  },
  mobileSliderTrack: {
    height: '100%',
    display: 'flex',
    transition: 'transform 0.3s ease-in-out'
  },
  mobileSliderSlide: {
    width: '100%',
    height: '100%',
    flexShrink: 0,
    position: 'relative'
  },
  mobileSliderImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    backgroundColor: theme.colors.lightGrey
  },
  mobileNavButton: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '30px',
    height: '30px',
    backgroundColor: theme.colors.primary,
    border: `1px solid ${theme.colors.grey}`,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#ffffff',
    zIndex: 2,
    padding: '9px 2px',
    transition: 'all 0.2s ease'
  },
  mobileNavLeft: {
    left: '0px'
  },
  mobileNavRight: {
    right: '0px'
  },
  mobileSlideCounter: {
    position: 'absolute',
    bottom: '10px',
    right: '10px',
    color: theme.colors.white,
    fontSize: '18px',
    fontWeight: '500',
    fontFamily: theme.fonts.body,
    textAlign: 'center'
  },
  // Media query for tablets (768px to 1024px)
  '@media (min-width: 768px) and (max-width: 1024px)': {
    container: {
      paddingTop: '10vh'
    }
  },
  // Media query for tablets (768px to 1024px)
  '@media (min-width: 520px) and (max-width: 768px)': {
    container: {
      paddingTop: '8vh'
    }
  }

  // ...existing styles...
}, { name: 'ProductDetail' })

export default ProductDetail
